import { useState } from 'react';

import { FileTextIcon, ImageIcon } from 'lucide-react';

import type { FileMimeType } from '@mds/shared/types/file';
import { cn } from '@mds/shared/lib/utils';

/**
 * A file's preview, or its type's icon when there is none yet — or when the
 * signed preview URL has expired by the time the image is asked for.
 *
 * `row` is the square in a table row, cropped to the top so a document shows
 * its heading; `large` is the whole preview in the file dialog.
 */
export default function FileThumb({
  url,
  mimeType,
  size = 'row',
}: {
  url: string | null | undefined;
  mimeType: FileMimeType;
  size?: 'row' | 'large';
}) {
  // Remembers which URL failed rather than whether one did, so a fresh URL gets its own try.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const Icon = mimeType === 'application/pdf' ? FileTextIcon : ImageIcon;
  const showImage = Boolean(url) && failedUrl !== url;

  if (size === 'large') {
    return (
      <div className="flex min-h-40 items-center justify-center overflow-hidden rounded-xl border border-hairline bg-raised">
        {showImage ? (
          <img
            src={url as string}
            alt=""
            onError={() => setFailedUrl(url ?? null)}
            className="block max-h-[320px] w-auto max-w-full object-contain"
          />
        ) : (
          <Icon className="size-12 text-ink-muted" strokeWidth={1.6} aria-hidden="true" />
        )}
      </div>
    );
  }

  return (
    <span
      className={cn(
        'flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-hairline',
        showImage ? 'bg-surface' : 'bg-raised',
      )}
    >
      {showImage ? (
        <img
          src={url as string}
          alt=""
          onError={() => setFailedUrl(url ?? null)}
          className="block size-full object-cover object-top"
        />
      ) : (
        <Icon className="size-5 text-ink-muted" strokeWidth={2} aria-hidden="true" />
      )}
    </span>
  );
}
